import * as React from "react";
import { Slider } from "@/components/ui/slider";
import { SimpleSelectPrimitive } from "@/components/ui/simple-select";
import { Smartphone, Monitor, Minus, Plus, AlertTriangle } from "lucide-react";
import { ControlOption } from "@/containers/RecordVideo/components/ControlOption";
import { VirtualBackgroundControl } from "./VirtualBackgroundControl";

interface Props {
  disableCameraChanges: boolean;
  speed: number;
  textSize: number;
  onChangeSpeed(speed: number): void;
  onChangeTextSize(textSize: number): void;

  onChangeCamera(deviceId: string): void;
  onChangeMicrophone(deviceId: string): void;

  cameraOptions: any[];
  microphoneOptions: any[];
  selectedCamera: string | undefined;
  selectedMicrophone: string | undefined;

  aspectRatio: number;
  onAspectRatioChange: (ar: number) => void;
  canChangeAspectRatio?: boolean;
  warningMessage?: string;

  // Virtual Background Props
  bgType: "none" | "blur" | "gradient" | "image";
  bgBlur: number;
  bgColors: string[];
  bgImage: string | null;
  bgLogo: string | null;
  onBgLogoChange: (logo: string | null) => void;
  bgLogoStyle: "none" | "watermark" | "backdrop" | "repeat";
  onBgLogoStyleChange: (style: "none" | "watermark" | "backdrop" | "repeat") => void;
  onBgTypeChange: (type: "none" | "blur" | "gradient" | "image") => void;
  onBgBlurChange: (blur: number) => void;
  onBgColorsChange: (colors: string[]) => void;
  onBgImageChange: (image: string | null) => void;

  hasPersistedSettings?: boolean;
  onResetSettings?: () => void;
}

const getTextSizeLabel = (size: number) => {
  if (size <= 18) return "Small";
  if (size <= 24) return "Medium";
  if (size <= 30) return "Large";
  return "Extra Large";
};

const getSpeedLabel = (speed: number) => {
  if (speed === 0) return "Paused";
  if (speed <= 15) return "Slow";
  if (speed <= 30) return "Normal";
  if (speed <= 45) return "Fast";
  return "Very Fast";
};

interface FormSelectProps {
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string | undefined) => void;
  items: Array<{ label: string; value: string }>;
  disabled?: boolean;
}

const FormSelect: React.FC<FormSelectProps> = ({
  placeholder = "Select...",
  value,
  onValueChange,
  items,
  disabled,
}) => {
  return (
    <SimpleSelectPrimitive
      value={value}
      onValueChange={val => onValueChange && onValueChange(val || undefined)}
      disabled={disabled}
    >
      <SimpleSelectPrimitive.Trigger className="flex h-9 w-full items-center justify-between rounded-md border border-gray-200 bg-background px-3 py-2 text-sm placeholder:text-muted-foreground hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 shadow-2xs transition-colors">
        <SimpleSelectPrimitive.Value placeholder={placeholder} className="truncate pr-2">
          {items.find(i => i.value === value)?.label}
        </SimpleSelectPrimitive.Value>
      </SimpleSelectPrimitive.Trigger>

      <SimpleSelectPrimitive.Content align="start" className="w-full">
        {items.map(item => (
          <SimpleSelectPrimitive.Item key={item.value} value={item.value} className="text-sm py-1.5 px-3">
            {item.label}
          </SimpleSelectPrimitive.Item>
        ))}
      </SimpleSelectPrimitive.Content>
    </SimpleSelectPrimitive>
  );
};

const ControlSectionFC: React.FC<Props> = ({
  disableCameraChanges,
  speed,
  textSize,
  onChangeSpeed,
  onChangeTextSize,
  onChangeCamera,
  onChangeMicrophone,
  cameraOptions,
  microphoneOptions,
  selectedCamera,
  selectedMicrophone,
  aspectRatio,
  onAspectRatioChange,
  canChangeAspectRatio,
  warningMessage,
  // Virtual Background Props
  bgType,
  bgBlur,
  bgColors,
  bgImage,
  bgLogo,
  onBgLogoChange,
  bgLogoStyle,
  onBgLogoStyleChange,
  onBgTypeChange,
  onBgBlurChange,
  onBgColorsChange,
  onBgImageChange,
  hasPersistedSettings,
  onResetSettings,
}) => {
  const MIN_SPEED = 0; // Slowest scroll
  const MAX_SPEED = 55; // Fastest scroll

  return (
    <div className="flex flex-col w-full text-left max-h-[calc(100vh-150px)] overflow-y-auto px-2 scrollbar-thin">
      {/* SECTION 1: Teleprompter */}
      <div className="flex flex-col w-full pb-4 border-b border-gray-200">
        {/* Text Size Slider */}
        <ControlOption
          title="Text size"
          action={
            <div className="flex items-center gap-1.5 select-none">
              <button
                type="button"
                className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
                onClick={() => onChangeTextSize(Math.max(15, textSize - 1))}
              >
                <Minus className="size-3.5" />
              </button>
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md min-w-14 text-center">
                {getTextSizeLabel(textSize)}
              </span>
              <button
                type="button"
                className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
                onClick={() => onChangeTextSize(Math.min(36, textSize + 1))}
              >
                <Plus className="size-3.5" />
              </button>
            </div>
          }
        >
          <div className="pt-2 px-1">
            <Slider
              min={15}
              max={36}
              step={1}
              value={[textSize]}
              onValueChange={val => {
                const newVal = Array.isArray(val) ? val[0] : val;
                onChangeTextSize(newVal);
              }}
            />
          </div>
        </ControlOption>

        {/* Scroll Speed Slider */}
        <ControlOption
          title="Scroll speed"
          action={
            <div className="flex items-center gap-1.5 select-none">
              <button
                type="button"
                className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
                onClick={() => onChangeSpeed(Math.max(0, speed - 5))}
              >
                <Minus className="size-3.5" />
              </button>
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md min-w-14 text-center">
                {getSpeedLabel(speed)}
              </span>
              <button
                type="button"
                className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
                onClick={() => onChangeSpeed(Math.min(55, speed + 5))}
              >
                <Plus className="size-3.5" />
              </button>
            </div>
          }
        >
          <div className="pt-2 px-1">
            <Slider
              min={MIN_SPEED}
              max={MAX_SPEED}
              step={5}
              value={[speed]}
              onValueChange={val => {
                const newVal = Array.isArray(val) ? val[0] : val;
                onChangeSpeed(newVal);
              }}
            />
          </div>
        </ControlOption>
      </div>

      {/* SECTION 2: Scene Setup */}
      <div className="flex flex-col w-full py-4 border-b border-gray-200">
        {/* Orientation Toggle */}
        <ControlOption title="Orientation">
          <div className="flex bg-gray-100 p-1 rounded-lg border border-gray-200/50 w-full mt-1.5">
            <button
              type="button"
              disabled={canChangeAspectRatio}
              onClick={() => onAspectRatioChange(9 / 16)}
              className={`flex-1 flex items-center justify-center py-2 rounded-md text-sm transition-all cursor-pointer font-medium ${
                aspectRatio === 9 / 16
                  ? "bg-white text-gray-900 shadow-sm font-semibold"
                  : "text-gray-500 hover:text-gray-900"
              } ${canChangeAspectRatio ? "opacity-50 cursor-not-allowed" : "hover:scale-[1.01]"}`}
            >
              <Smartphone className="size-4 mr-2 shrink-0" />
              <span>Vertical (9:16)</span>
            </button>
            <button
              type="button"
              disabled={canChangeAspectRatio}
              onClick={() => onAspectRatioChange(4 / 3)}
              className={`flex-1 flex items-center justify-center py-2 rounded-md text-sm transition-all cursor-pointer font-medium ${
                aspectRatio === 4 / 3
                  ? "bg-white text-gray-900 shadow-sm font-semibold"
                  : "text-gray-500 hover:text-gray-900"
              } ${canChangeAspectRatio ? "opacity-50 cursor-not-allowed" : "hover:scale-[1.01]"}`}
            >
              <Monitor className="size-4 mr-2 shrink-0" />
              <span>Horizontal (4:3)</span>
            </button>
          </div>
        </ControlOption>

        {/* Virtual Background Controls */}
        <VirtualBackgroundControl
          aspectRatio={aspectRatio}
          bgType={bgType}
          bgBlur={bgBlur}
          bgColors={bgColors}
          bgImage={bgImage}
          bgLogo={bgLogo}
          onBgLogoChange={onBgLogoChange}
          bgLogoStyle={bgLogoStyle}
          onBgLogoStyleChange={onBgLogoStyleChange}
          onBgTypeChange={onBgTypeChange}
          onBgBlurChange={onBgBlurChange}
          onBgColorsChange={onBgColorsChange}
          onBgImageChange={onBgImageChange}
        />
      </div>

      {/* SECTION 3: Devices */}
      <div className="flex flex-col w-full pt-4 pb-2">
        {/* Camera Selector */}
        {cameraOptions && cameraOptions.length > 0 && (
          <ControlOption title="Camera">
            <FormSelect
              placeholder="Select Camera"
              onValueChange={val => val && onChangeCamera(val)}
              items={cameraOptions.map(opt => ({
                label: opt.text || "Camera",
                value: String(opt.value),
              }))}
              value={selectedCamera}
              disabled={disableCameraChanges}
            />
          </ControlOption>
        )}

        {/* Microphone Selector */}
        {microphoneOptions && microphoneOptions.length > 0 && (
          <ControlOption title="Microphone">
            <FormSelect
              placeholder="Select Microphone"
              onValueChange={val => val && onChangeMicrophone(val)}
              items={microphoneOptions.map(opt => ({
                label: opt.text || "Microphone",
                value: String(opt.value),
              }))}
              value={selectedMicrophone}
              disabled={disableCameraChanges}
            />
          </ControlOption>
        )}

        {/* Reset Settings Action Link */}
        {hasPersistedSettings && onResetSettings && (
          <div className="flex justify-center pt-2.5 mt-2.5 select-none">
            <button
              type="button"
              onClick={onResetSettings}
              className="text-[11px] font-medium text-slate-400 hover:text-red-500 cursor-pointer transition-colors hover:underline flex items-center gap-1 leading-none"
              title="Reset all settings and device preferences to factory defaults"
            >
              Reset to Defaults
            </button>
          </div>
        )}
      </div>

      {warningMessage && warningMessage.length > 0 && (
        <div className="mt-3 rounded-lg border border-red-200 p-3 flex items-center bg-red-50/50 text-red-600 text-sm leading-normal shadow-3xs">
          <AlertTriangle className="size-4 mr-2 text-orange-500 shrink-0" />
          {warningMessage}
        </div>
      )}
    </div>
  );
};

export const ControlSection = ControlSectionFC;
